import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon as IconifyIcon } from '@iconify/react';
import { useI18n } from '../../lib/i18n';

const ContactLocationsSection4 = ({ className }: { className?: string }) => {
  const [copied, setCopied] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useI18n()

  // Check opening hours (07:00 - 17:00)
  useEffect(() => {
    const checkTime = () => {
      const now = new Date();
      const hours = now.getHours();
      // Open between 7 and 17 (5 PM)
      setIsOpen(hours >= 7 && hours < 17);
    };
    checkTime();
    const interval = setInterval(checkTime, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  const handleCopy = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <section className={`relative py-20 md:py-32 bg-[#F4F5F7] overflow-hidden ${className}`}>
      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16 max-w-3xl mx-auto"
        >
          <span className="inline-block px-3 py-1 mb-4 text-xs font-semibold tracking-wider text-[#0052CC] uppercase bg-[#0052CC]/10 rounded-full">
            {t.contactSection.badge}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#172B4D] mb-4 tracking-tight">
            {t.contactSection.title}
          </h2>
          <p className="text-lg text-[#172B4D]/70">
            {t.contactSection.subtitle}
          </p>
        </motion.div>

        {/* Bento Grid */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-6"
        >
          
          {/* LEFT COLUMN: Info Stack (40% width on desktop -> col-span-5) */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            
            {/* Card A: Support */}
            <motion.div 
              variants={itemVariants}
              whileHover={{ y: -4, borderColor: "rgba(0, 82, 204, 0.2)" }}
              className="bg-white rounded-2xl p-6 md:p-8 border border-[#172B4D]/5 shadow-sm transition-all duration-300 group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#0052CC]/5 flex items-center justify-center text-[#0052CC]">
                  <IconifyIcon icon="ph:phone-call-fill" width="24" height="24" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#172B4D]/40 bg-[#F4F5F7] px-2 py-1 rounded">
                  {t.contactSection.support}
                </span>
              </div>

              <h3 className="text-sm font-semibold uppercase tracking-wider text-[#172B4D]/60 mb-2">
                {t.contactSection.customerService}
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between group/item">
                  <a href="tel:+4917632655029" className="text-lg md:text-xl font-medium text-[#172B4D] hover:text-[#0052CC] transition-colors">
                    +49 (0) 176 326 55 029
                  </a>
                </div>
                
                <div 
                  onClick={() => handleCopy("info@parkboxfrankfurt.com", "email")}
                  className="flex items-center justify-between cursor-pointer group/item"
                >
                  <span className="text-lg md:text-xl font-medium text-[#172B4D] group-hover/item:text-[#0052CC] transition-colors">
                    info@parkboxfrankfurt.com
                  </span>
                  <div className="relative">
                    <AnimatePresence>
                      {copied === "email" ? (
                        <motion.span
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0 }}
                          className="text-xs font-medium text-green-600 absolute right-8 top-1/2 -translate-y-1/2 whitespace-nowrap"
                        >
                          {t.contactSection.copied}
                        </motion.span>
                      ) : null}
                    </AnimatePresence>
                    <IconifyIcon 
                      icon={copied === "email" ? "ph:check-circle-fill" : "ph:copy"} 
                      className={`w-5 h-5 transition-colors ${copied === "email" ? "text-green-500" : "text-[#172B4D]/30 group-hover/item:text-[#0052CC]"}`} 
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Card B: Address */}
            <motion.div 
              variants={itemVariants}
              whileHover={{ y: -4, borderColor: "rgba(0, 82, 204, 0.2)" }}
              className="bg-white rounded-2xl p-6 md:p-8 border border-[#172B4D]/5 shadow-sm transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#0052CC]/5 flex items-center justify-center text-[#0052CC]">
                  <IconifyIcon icon="ph:map-pin-fill" width="24" height="24" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#172B4D]/40 bg-[#F4F5F7] px-2 py-1 rounded">
                  {t.contactSection.location}
                </span>
              </div>

              <h3 className="text-sm font-semibold uppercase tracking-wider text-[#172B4D]/60 mb-2">
                {t.contactSection.parkingAddress}
              </h3>
              
              <p className="text-lg md:text-xl font-medium text-[#172B4D] mb-4 leading-snug">
                Südring 10<br/>
                65795 Hattersheim am Main
              </p>

              <a
                href="https://maps.app.goo.gl/pa7NAhtyDFmiS22o7" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm font-semibold text-[#0052CC] hover:text-[#0747A6] group/link"
              >
                <span className="relative">
                  {t.contactSection.planRoute}
                  <span className="absolute left-0 bottom-0 w-0 h-px bg-[#0052CC] transition-all duration-300 group-hover/link:w-full"></span>
                </span>
                <IconifyIcon icon="ph:arrow-right" className="ml-1 w-4 h-4 transform group-hover/link:translate-x-1 transition-transform" />
              </a>
            </motion.div>

            {/* Card C: Hours */}
            <motion.div 
              variants={itemVariants}
              whileHover={{ y: -4, borderColor: "rgba(0, 82, 204, 0.2)" }}
              className="bg-white rounded-2xl p-6 md:p-8 border border-[#172B4D]/5 shadow-sm transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#0052CC]/5 flex items-center justify-center text-[#0052CC]">
                  <IconifyIcon icon="ph:clock-fill" width="24" height="24" />
                </div>
                <div className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-bold uppercase tracking-wider ${isOpen ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  <span className={`w-2 h-2 rounded-full ${isOpen ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></span>
                  {isOpen ? t.contactSection.nowOpen : t.contactSection.closed}
                </div>
              </div>
              
              <h3 className="text-sm font-semibold uppercase tracking-wider text-[#172B4D]/60 mb-2">
                {t.contactSection.openingHours}
              </h3>

              <p className="text-lg md:text-xl font-medium text-[#172B4D] mb-1">
                {t.contactSection.hours}
              </p>
              <p className="text-sm text-[#172B4D]/60">
                {t.contactSection.dailyOpen}
              </p>
            </motion.div>

          </div>

          {/* RIGHT COLUMN: Map Visualization (60% width on desktop -> col-span-7) */}
          <motion.div 
            variants={itemVariants}
            className="lg:col-span-7 h-full min-h-[400px] lg:min-h-0"
          >
            <motion.div 
              whileHover={{ scale: 1.005 }}
              className="relative w-full h-full rounded-2xl overflow-hidden border border-[#172B4D]/5 shadow-sm group"
            >
              {/* Google Maps Embed */}
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2557.5!2d8.4724!3d50.0588!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47bd0a3c8e8b8b8b%3A0x1234567890abcdef!2sS%C3%BCdring%2010%2C%2065795%20Hattersheim%20am%20Main!5e0!3m2!1sde!2sde!4v1706700000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Park-Box Frankfurt Standort Karte"
              />
              
              {/* Overlay Gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#172B4D]/20 to-transparent pointer-events-none" />

              {/* Floating Info Card on Map */}
            </motion.div>
          </motion.div>

        </motion.div>
      </div>
    </section>
  );
};

export default ContactLocationsSection4;